/* import __COLOCATED_TEMPLATE__ from './fin-phone-behavior.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import {
  type FinVoiceVoice,
  DEFAULT_VOICE,
  FIN_VOICE_11LABS_VOICES,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import {
  DEFAULT_VOICE_LANGUAGE,
  type FinVoiceVoiceLanguage,
  FIN_VOICE_VOICE_LANGUAGE_OPTIONS,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updateFinBehavior: (
      voice?: FinVoiceVoice,
      greeting?: string,
      language?: FinVoiceVoiceLanguage,
    ) => Promise<void>;
  };
}

export default class FinPhoneBehavior extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare notificationsService: $TSFixMe;

  @tracked voice?: FinVoiceVoice;
  @tracked selectedLanguage: FinVoiceVoiceLanguage = DEFAULT_VOICE_LANGUAGE;
  @tracked greeting = '';

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.resetFinBehavior();
  }

  get savingDisabled() {
    return Boolean(!this.voice || !this.greeting || this.numberConfiguration?.isSaving);
  }

  get errorMessage() {
    if (!this.voice) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.error-no-voice',
      );
    }

    if (!this.greeting) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-behavior.error-no-greeting',
      );
    }

    return undefined;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get savedVoice() {
    let savedVoice = this.numberConfiguration?.voice;

    if (!savedVoice || !FIN_VOICE_11LABS_VOICES.find((voice) => voice.value === savedVoice)) {
      return undefined;
    }

    return savedVoice;
  }

  get savedGreeting() {
    return this.numberConfiguration?.greeting;
  }

  get activeVoiceLabel() {
    let savedVoice = '';
    let savedLanguage = this.selectedLanguage || DEFAULT_VOICE_LANGUAGE;

    if (this.savedVoice) {
      let voiceData = FIN_VOICE_11LABS_VOICES.find((voice) => voice.value === this.savedVoice);
      savedVoice = voiceData?.text || '';
    }

    let languageData = FIN_VOICE_VOICE_LANGUAGE_OPTIONS.find(
      (language) => language.value === savedLanguage,
    );
    let savedLanguageText = languageData?.text ? this.intl.t(languageData.text) : '';

    return [savedLanguageText, savedVoice].join(' • ');
  }

  @action
  resetFinBehavior() {
    this.voice = this.savedVoice || DEFAULT_VOICE;
    this.greeting = this.savedGreeting || '';
    if (
      this.numberConfiguration?.handoverConfiguration.default &&
      this.numberConfiguration.handoverConfiguration.default.type === 'handover-resume-ivr-workflow'
    ) {
      this.selectedLanguage =
        this.numberConfiguration.handoverConfiguration.default.config.ivrLocale ||
        DEFAULT_VOICE_LANGUAGE;
    } else {
      this.selectedLanguage = DEFAULT_VOICE_LANGUAGE;
    }
  }

  @action
  onVoiceLanguageChange(voice: FinVoiceVoice, language: FinVoiceVoiceLanguage) {
    this.voice = voice;
    this.selectedLanguage = language;
  }

  @action
  updateGreeting(event: Event) {
    let target = event.target as HTMLTextAreaElement;
    this.greeting = target.value;
  }

  @action
  async updateFinBehavior() {
    if (!this.voice && !this.greeting) {
      return;
    }

    await this.args.updateFinBehavior(this.voice, this.greeting, this.selectedLanguage);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinVoice::Editor::FinPhoneBehavior': typeof FinPhoneBehavior;
  }
}
